export type BootOverlayState =
  | "BOOT_INTRO"
  | "MAIN_MENU"
  | "ASSET_ERROR";

export type BootStartResult = "READY" | "REDIRECTED" | "ASSET_ERROR";

export const BB_DISCLAIMER_ACCEPTED_KEY = "bb_disclaimer_accepted";
export const BB_DISCLAIMER_ACCEPTED_AT_KEY = "bb_disclaimer_accepted_at";
export const BB_A2HS_PROMPT_SHOWN_KEY = "bb_a2hs_prompt_shown";
export const BOOT_INTRO_TIMING_MS = {
  mainLogoFadeDelay: 300,
  mainLogoFadeDuration: 1300,
  devBrandFadeDelay: 2000,
  devBrandFadeDuration: 1200,
  totalDuration: 4000
} as const;
const IFRAME_ALLOWLIST_SAFETY_TIMEOUT_MS = 14000;

const INTRO_MAIN_LOGO_FADE_DELAY_MS = BOOT_INTRO_TIMING_MS.mainLogoFadeDelay;
const INTRO_MAIN_LOGO_FADE_DURATION_MS = BOOT_INTRO_TIMING_MS.mainLogoFadeDuration;
const INTRO_DEV_BRAND_FADE_DELAY_MS = BOOT_INTRO_TIMING_MS.devBrandFadeDelay;
const INTRO_DEV_BRAND_FADE_DURATION_MS = BOOT_INTRO_TIMING_MS.devBrandFadeDuration;
const INTRO_COMPLETE_MS = BOOT_INTRO_TIMING_MS.totalDuration;

const GAME_LOGO_CANDIDATES = [
  "/assets/branding/logo_game.png",
  "/assets/branding/logo_game.svg"
] as const;
const DEV_HEAD_LOGO_PATH = "/assets/branding/logo_beaststudiolab_head.svg";
const DEV_WORDMARK_LOGO_PATH = "/assets/branding/logo_beaststudiolab_wordmark.svg";

import { computeCanvasDimensions } from "../game/platform";

const CHARACTER_COUNT = 9;
const CHARACTER_POSITIONS: Array<{ top: string; left: string }> = [
  { top: "8%",  left: "10%" },
  { top: "12%", left: "75%" },
  { top: "30%", left: "5%" },
  { top: "28%", left: "85%" },
  { top: "55%", left: "8%" },
  { top: "52%", left: "82%" },
  { top: "72%", left: "15%" },
  { top: "70%", left: "72%" },
  { top: "88%", left: "45%" },
];

interface PreloadedBrandingAssets {
  gameLogoPath: string;
  devHeadPath: string;
  devWordmarkPath: string;
}

interface BootOverlayManagerOptions {
  host: HTMLElement;
}

export function shouldConsumeOverlayBackdropEvent(
  isBlockingInput: boolean,
  isBackdropTarget: boolean
): boolean {
  return isBlockingInput && isBackdropTarget;
}

export class BootFlowController {
  private state: BootOverlayState = "BOOT_INTRO";
  private missingAssets: string[] = [];

  getState(): BootOverlayState {
    return this.state;
  }

  getMissingAssets(): string[] {
    return [...this.missingAssets];
  }

  isBlockingInput(): boolean {
    return this.state !== "MAIN_MENU";
  }

  failAssets(missingAssets: string[]): void {
    this.missingAssets = [...missingAssets];
    this.state = "ASSET_ERROR";
  }

  onIntroComplete(): BootOverlayState {
    if (this.state !== "BOOT_INTRO") {
      return this.state;
    }
    this.state = "MAIN_MENU";
    return this.state;
  }
}

export class BootOverlayManager {
  private readonly host: HTMLElement;
  private readonly flow: BootFlowController;
  private readonly root: HTMLDivElement;
  private readonly stateContainer: HTMLDivElement;
  private brandingAssets: PreloadedBrandingAssets | null = null;
  private disposed = false;
  private attached = false;
  private started = false;
  private pendingTimers = new Set<number>();
  private assetGateTimeoutMs = IFRAME_ALLOWLIST_SAFETY_TIMEOUT_MS;
  private startPromise: Promise<BootStartResult> | null = null;
  private resolveStart: ((result: BootStartResult) => void) | null = null;
  private startResult: BootStartResult | null = null;
  private readonly onWindowKeyDownCapture = (event: KeyboardEvent): void => {
    if (!this.isBlockingInput()) {
      return;
    }
    if (event.key === "F5" || (event.ctrlKey && event.key.toLowerCase() === "r")) {
      return;
    }
    if (event.target instanceof Node && this.root.contains(event.target)) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  constructor(options: BootOverlayManagerOptions) {
    this.host = options.host;
    this.flow = new BootFlowController();

    this.root = document.createElement("div");
    this.root.className = "boot-overlay-root";
    this.root.style.display = "none";
    this.root.tabIndex = -1;
    this.root.setAttribute("aria-live", "polite");
    this.root.setAttribute("aria-label", "Boot overlay");

    const dims = computeCanvasDimensions();
    this.root.style.setProperty("--canvas-h", String(dims.height));

    this.stateContainer = document.createElement("div");
    this.stateContainer.className = "boot-overlay-state";
    this.root.appendChild(this.stateContainer);

    const consumeBackdropOnly = (event: Event): void => {
      if (!shouldConsumeOverlayBackdropEvent(this.isBlockingInput(), event.target === this.root)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    this.root.addEventListener("pointerdown", consumeBackdropOnly, true);
    this.root.addEventListener("click", consumeBackdropOnly, true);
  }

  attach(): void {
    if (this.attached || this.disposed) {
      return;
    }
    this.host.appendChild(this.root);
    window.addEventListener("keydown", this.onWindowKeyDownCapture, true);
    this.attached = true;
  }

  async start(): Promise<BootStartResult> {
    if (this.disposed) {
      return this.startResult ?? "ASSET_ERROR";
    }
    if (this.startPromise != null) {
      return this.startPromise;
    }
    if (this.started) {
      return this.startResult ?? "ASSET_ERROR";
    }
    this.started = true;
    this.startPromise = new Promise((resolve) => {
      this.resolveStart = resolve;
    });
    if (!this.attached) {
      this.attach();
    }

    const preload = await this.preloadStrictAssets();
    if (!preload.ok) {
      this.flow.failAssets(preload.missingAssets);
      this.renderCurrentState();
      this.finishStart("ASSET_ERROR");
      return this.startPromise;
    }

    this.brandingAssets = preload.assets;
    this.renderCurrentState();
    this.startIntroTimeline();
    return this.startPromise;
  }

  isBlockingInput(): boolean {
    if (this.disposed) {
      return false;
    }
    return this.flow.isBlockingInput();
  }

  destroy(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    if (this.startPromise != null && this.startResult == null) {
      this.finishStart("ASSET_ERROR");
    }
    this.clearAllTimers();
    window.removeEventListener("keydown", this.onWindowKeyDownCapture, true);
    this.root.remove();
  }

  private renderCurrentState(): void {
    if (this.disposed) {
      return;
    }
    this.clearAllTimers();
    const state = this.flow.getState();
    this.root.style.display = state === "MAIN_MENU" ? "none" : "flex";
    this.stateContainer.replaceChildren();

    if (state === "MAIN_MENU") {
      this.finishStart("READY");
      return;
    }

    if (state === "BOOT_INTRO") {
      this.renderIntro();
      return;
    }

    this.renderAssetError();
    this.finishStart("ASSET_ERROR");
  }

  private renderIntro(): void {
    if (this.brandingAssets == null) {
      this.flow.failAssets(["branding assets unresolved"]);
      this.renderCurrentState();
      return;
    }

    const intro = document.createElement("section");
    intro.className = "boot-intro-screen";

    const gameLogo = document.createElement("img");
    gameLogo.className = "boot-intro-game-logo";
    gameLogo.style.transitionDuration = `${INTRO_MAIN_LOGO_FADE_DURATION_MS}ms`;
    gameLogo.src = this.brandingAssets.gameLogoPath;
    gameLogo.alt = "BitBeast Block Party";
    intro.appendChild(gameLogo);

    const devRow = document.createElement("div");
    devRow.className = "boot-intro-dev-row";
    devRow.style.transitionDuration = `${INTRO_DEV_BRAND_FADE_DURATION_MS}ms`;

    const devHead = document.createElement("img");
    devHead.className = "boot-intro-dev-head";
    devHead.src = this.brandingAssets.devHeadPath;
    devHead.alt = "BeastStudioLab icon";

    const devWordmark = document.createElement("img");
    devWordmark.className = "boot-intro-dev-wordmark";
    devWordmark.src = this.brandingAssets.devWordmarkPath;
    devWordmark.alt = "BeastStudioLab";

    devRow.appendChild(devHead);
    devRow.appendChild(devWordmark);
    intro.appendChild(devRow);

    for (let i = 1; i <= CHARACTER_COUNT; i++) {
      const char = document.createElement("img");
      char.className = "boot-intro-character";
      char.src = `/assets/characters/character_${i}_hd.png`;
      char.alt = "";
      char.draggable = false;
      const pos = CHARACTER_POSITIONS[i - 1];
      char.style.top = pos.top;
      char.style.left = pos.left;
      // Start at center, glide outward with logo
      char.style.translate = `${50 - parseFloat(pos.left)}vw ${50 - parseFloat(pos.top)}vh`;
      char.style.scale = "0.3";
      char.style.opacity = "0";
      const burstDelay = 250 + i * 35;
      char.style.transition = `translate 600ms cubic-bezier(0.16,1,0.3,1) ${burstDelay}ms, scale 600ms cubic-bezier(0.16,1,0.3,1) ${burstDelay}ms, opacity 250ms ease-out ${burstDelay}ms`;
      char.dataset.burstReady = "1";
      const floatDuration = 8 + Math.random() * 7;
      const floatDelay = Math.random() * -floatDuration;
      char.dataset.floatAnim = `char-float ${floatDuration.toFixed(1)}s ease-in-out ${floatDelay.toFixed(1)}s infinite`;
      intro.appendChild(char);
    }

    this.stateContainer.appendChild(intro);
  }

  private startIntroTimeline(): void {
    const gameLogo = this.stateContainer.querySelector<HTMLElement>(".boot-intro-game-logo");
    const devRow = this.stateContainer.querySelector<HTMLElement>(".boot-intro-dev-row");
    if (gameLogo == null || devRow == null) {
      return;
    }

    this.schedule(INTRO_MAIN_LOGO_FADE_DELAY_MS, () => {
      gameLogo.classList.add("is-visible");
      // Burst characters outward from center
      const chars = this.stateContainer.querySelectorAll<HTMLElement>(".boot-intro-character[data-burst-ready]");
      requestAnimationFrame(() => {
        for (const c of chars) {
          c.style.translate = "0 0";
          c.style.scale = "1";
          c.style.opacity = "0.85";
          c.addEventListener("transitionend", () => {
            // Switch to float animation after burst completes
            const floatAnim = c.dataset.floatAnim;
            if (floatAnim) {
              c.style.transition = "none";
              c.style.animation = floatAnim;
            }
          }, { once: true });
        }
      });
    });

    this.schedule(INTRO_DEV_BRAND_FADE_DELAY_MS, () => {
      devRow.classList.add("is-visible");
    });

    this.schedule(INTRO_COMPLETE_MS, () => {
      this.flow.onIntroComplete();
      this.renderCurrentState();
    });
  }

  private finishStart(result: BootStartResult): void {
    if (this.startResult != null) {
      return;
    }
    this.startResult = result;
    this.resolveStart?.(result);
    this.resolveStart = null;
  }

  private renderAssetError(): void {
    const screen = document.createElement("section");
    screen.className = "boot-asset-error-screen";

    const card = document.createElement("div");
    card.className = "boot-asset-error-card";

    const title = document.createElement("h1");
    title.className = "boot-asset-error-title";
    title.textContent = "Missing Required Boot Assets";

    const body = document.createElement("p");
    body.className = "boot-asset-error-body";
    body.textContent = "Startup is blocked until required branding files are available.";

    const list = document.createElement("ul");
    list.className = "boot-asset-error-list";
    for (const asset of this.flow.getMissingAssets()) {
      const item = document.createElement("li");
      item.textContent = asset;
      list.appendChild(item);
    }

    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(list);
    screen.appendChild(card);
    this.stateContainer.appendChild(screen);
  }

  private schedule(delayMs: number, callback: () => void): void {
    if (this.disposed) {
      return;
    }
    const timerId = window.setTimeout(() => {
      this.pendingTimers.delete(timerId);
      if (this.disposed) {
        return;
      }
      callback();
    }, delayMs);
    this.pendingTimers.add(timerId);
  }

  private clearAllTimers(): void {
    for (const timerId of this.pendingTimers) {
      window.clearTimeout(timerId);
    }
    this.pendingTimers.clear();
  }

  private async preloadStrictAssets():
    Promise<{ ok: true; assets: PreloadedBrandingAssets } | { ok: false; missingAssets: string[] }> {
    const missingAssets: string[] = [];

    const gameLogoPath = await this.preloadFirstAvailableImage([...GAME_LOGO_CANDIDATES]);
    if (gameLogoPath == null) {
      missingAssets.push("public/assets/branding/logo_game.png or public/assets/branding/logo_game.svg");
    }

    const devHeadOk = await this.preloadImage(DEV_HEAD_LOGO_PATH);
    if (!devHeadOk) {
      missingAssets.push("public/assets/branding/logo_beaststudiolab_head.svg");
    }

    const devWordmarkOk = await this.preloadImage(DEV_WORDMARK_LOGO_PATH);
    if (!devWordmarkOk) {
      missingAssets.push("public/assets/branding/logo_beaststudiolab_wordmark.svg");
    }

    if (missingAssets.length > 0) {
      return { ok: false, missingAssets };
    }

    if (gameLogoPath == null) {
      return { ok: false, missingAssets: ["asset preload did not complete"] };
    }

    return {
      ok: true,
      assets: {
        gameLogoPath,
        devHeadPath: DEV_HEAD_LOGO_PATH,
        devWordmarkPath: DEV_WORDMARK_LOGO_PATH
      }
    };
  }

  private async preloadFirstAvailableImage(paths: string[]): Promise<string | null> {
    for (const path of paths) {
      const ok = await this.preloadImage(path);
      if (ok) {
        return path;
      }
    }
    return null;
  }

  private preloadImage(path: string): Promise<boolean> {
    return new Promise((resolve) => {
      const image = new Image();
      let settled = false;
      let timeoutId = 0;
      const settle = (ok: boolean): void => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(ok);
      };

      image.onload = () => settle(true);
      image.onerror = () => settle(false);
      image.src = path;
      timeoutId = window.setTimeout(() => settle(false), this.assetGateTimeoutMs);
    });
  }
}

